import { useResizeObserver } from '@folklore/hooks';
import { animated } from '@react-spring/web';
import isFunction from 'lodash-es/isFunction';
import { ForwardedRef, ReactElement, ElementType, CSSProperties, ReactNode } from 'react';

import { getPrefixedProps } from '../../lib/utils';

export type ListItem = Record<string, unknown> | null;

export interface ListCardProps extends CardProps {
    cardComponent?: ElementType;
    [key: string]: unknown;
}

export interface ListProps<T = ListItem> {
    items?: T[] | null;
    recommendationId?: string | null;
    clickRef?: string | null;
    header?: ReactNode;
    footer?: ReactNode;
    cardComponent: ElementType;
    cardTheme?: CardTheme | LegacyCardTheme | null;
    cardSize?: string | null;
    cardWidth?: number | null;
    cardRatio?: string | number | null;
    cardResponsive?: boolean;
    cardImageLoading?: string | null;
    cardImageLazyLoadingStartIndex?: number | null;
    cardWith?: string | string[] | null;
    cardWithout?: string | string[] | null;
    cardProps?: ListCardProps | ((item: T, index: number) => ListCardProps) | null;
    cardLinkQuery?: Record<string, unknown> | null;
    withBorder?: boolean;
    className?: string | null;
    itemsClassName?: string | null;
    itemsStyle?: CSSProperties | null;
    itemClassName?: string | null;
    itemStyle?: CSSProperties | ((item: T, index: number) => CSSProperties) | null;
    cardClassName?: string | null;
    thumbnailClassName?: string | null;
    onClickCard?: ((e: MouseEvent, item: T, index: number) => void) | null;
    itemsRef?: ForwardedRef<HTMLDivElement>;
}

function List<T = ListItem>({
    items = null,
    recommendationId = null,
    clickRef = null,
    header = null,
    footer = null,
    cardComponent: CardComponent,
    cardTheme = null,
    cardSize = null,
    cardWidth = null,
    cardRatio = null,
    cardResponsive = false,
    cardImageLoading = null,
    cardImageLazyLoadingStartIndex = null,
    cardWith = null,
    cardWithout = null,
    cardProps = null,
    cardLinkQuery = null,
    withBorder = false,
    className = null,
    itemsClassName = null,
    itemsStyle = null,
    itemClassName = null,
    itemStyle = null,
    cardClassName = null,
    thumbnailClassName = null,
    onClickCard = null,
    itemsRef = null,
    ref: containerRef = null,
}: ListProps<T> & { ref?: ForwardedRef<HTMLDivElement> | null }) {
    const {
        ref: firstCardResizeRef,
        entry: { contentRect: firstCardContentRect = null },
    } = useResizeObserver({
        disabled: cardWidth !== null,
    });
    const { width: firstCardWidth = null } = firstCardContentRect || {};

    const generalProps: {
        theme?: CardTheme | LegacyCardTheme;
        size?: string;
        ratio?: string | number;
        responsive?: boolean;
    } = {};
    if (cardTheme !== null) {
        generalProps.theme = cardTheme;
    }
    if (cardSize !== null) {
        generalProps.size = cardSize;
    }
    if (cardRatio !== null) {
        generalProps.ratio = cardRatio;
    }
    if (cardResponsive !== null) {
        generalProps.responsive = cardResponsive;
    }

    if (items === null || items.length === 0) {
        return null;
    }

    return (
        <div
            className={className}
            ref={containerRef}
            data-recommendation-id={recommendationId}
            data-click-ref={clickRef}
        >
            {header}
            <animated.div className={itemsClassName} style={itemsStyle} ref={itemsRef}>
                {(items || []).map((it, index) => {
                    const { recommendationId = null } = it || {};
                    const { cardComponent: CustomCardComponent = null, ...finalCardProps } =
                        (isFunction(cardProps) ? cardProps(it, index) : cardProps) || {};
                    const FinalCardComponent = CustomCardComponent || CardComponent;
                    return (
                        <animated.div
                            className={itemClassName}
                            key={`item-${index}`}
                            style={isFunction(itemStyle) ? itemStyle(it, index) : itemStyle}
                            data-recommendation-id={recommendationId}
                            ref={
                                index === 0
                                    ? (firstCardResizeRef as ForwardedRef<HTMLDivElement>)
                                    : null
                            }
                        >
                            <FinalCardComponent
                                {...generalProps}
                                responsive={cardResponsive}
                                width={cardWidth || firstCardWidth}
                                withBorder={withBorder}
                                placeholder={it === null}
                                onClick={
                                    onClickCard !== null ? (e) => onClickCard(e, it, index) : null
                                }
                                linkQuery={cardLinkQuery}
                                {...it}
                                {...getPrefixedProps(cardWith, 'with')}
                                {...getPrefixedProps(cardWithout, 'without')}
                                {...finalCardProps}
                                imageLoading={
                                    cardImageLoading ||
                                    (cardImageLazyLoadingStartIndex !== null &&
                                    index >= cardImageLazyLoadingStartIndex
                                        ? 'lazy'
                                        : null)
                                }
                                className={cardClassName}
                                thumbnailClassName={thumbnailClassName}
                            />
                        </animated.div>
                    );
                })}
            </animated.div>
            {footer}
        </div>
    );
}

export default List as <T = ListItem>(
    props: ListProps<T> & { ref?: ForwardedRef<HTMLDivElement> },
) => ReactElement;
